/* Sovrapposizioni: foglio dal basso, riquadro centrato, avvisi.
 *
 * Una sola implementazione per entrambe le larghezze: sotto i 720px la
 * sovrapposizione sale dal basso, sopra si centra. È il CSS a decidere, non un
 * ramo JavaScript — così l'animazione non riparte quando si gira il telefono.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { IconaChiudi, Pulsante } from './Base';

/* ---------- guscio comune ---------- */

function usaChiusuraConEsc(onChiudi: () => void, attivo: boolean) {
  useEffect(() => {
    if (!attivo) return;
    const suTasto = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onChiudi();
    };
    document.addEventListener('keydown', suTasto);
    /* Il corpo sotto non deve scorrere: su telefono lo scorrimento "passa
       attraverso" il foglio e si perde il punto in cui si era. */
    const precedente = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', suTasto);
      document.body.style.overflow = precedente;
    };
  }, [onChiudi, attivo]);
}

interface PropsSovrapposizione {
  aperta: boolean;
  onChiudi: () => void;
  titolo: string;
  sottotitolo?: string;
  /** Riga di pulsanti in fondo, sempre raggiungibile col pollice. */
  pieda?: ReactNode;
  larga?: boolean;
  children: ReactNode;
}

function Guscio({
  aperta,
  onChiudi,
  titolo,
  sottotitolo,
  pieda,
  children,
  classeVelo,
  classeRiquadro,
}: PropsSovrapposizione & { classeVelo: string; classeRiquadro: string }) {
  usaChiusuraConEsc(onChiudi, aperta);
  if (!aperta) return null;

  return createPortal(
    <div className={`sb-velo ${classeVelo}`} onClick={onChiudi} role="presentation">
      <div
        className={classeRiquadro}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={titolo}
      >
        <div className="sb-sovrapposizione__testata">
          <div style={{ flex: 1 }}>
            <div className="sb-sovrapposizione__titolo">{titolo}</div>
            {sottotitolo && <div className="sb-sovrapposizione__sottotitolo">{sottotitolo}</div>}
          </div>
          <Pulsante variante="icona" onClick={onChiudi} aria-label="Chiudi">
            <IconaChiudi dim={16} />
          </Pulsante>
        </div>

        <div className="sb-sovrapposizione__corpo">{children}</div>

        {pieda && <div className="sb-sovrapposizione__pieda">{pieda}</div>}
      </div>
    </div>,
    document.body,
  );
}

/** Foglio dal basso: filtri e scelte lunghe. Sempre dal basso, a ogni larghezza. */
export function Foglio(props: PropsSovrapposizione) {
  return <Guscio {...props} classeVelo="sb-velo--foglio" classeRiquadro="sb-foglio" />;
}

/** Riquadro: dal basso su telefono, centrato da 720px in su. */
export function Modale(props: PropsSovrapposizione) {
  return (
    <Guscio
      {...props}
      classeVelo="sb-velo--modale"
      classeRiquadro={`sb-modale${props.larga ? ' sb-modale--larga' : ''}`}
    />
  );
}

/* ---------- avvisi temporanei ---------- */

export interface Avviso {
  id: number;
  testo: string;
  esito: 'riuscito' | 'fallito' | 'neutro';
}

export function usaAvvisi() {
  const [avvisi, setAvvisi] = useState<Avviso[]>([]);
  const contatore = useRef(0);

  const mostra = useCallback((testo: string, esito: Avviso['esito'] = 'riuscito') => {
    const id = ++contatore.current;
    setAvvisi((a) => [...a, { id, testo, esito }]);
    setTimeout(() => setAvvisi((a) => a.filter((x) => x.id !== id)), 3200);
  }, []);

  return { avvisi, mostra };
}

export function Avvisi({ avvisi }: { avvisi: Avviso[] }) {
  if (avvisi.length === 0) return null;
  return createPortal(
    <div className="sb-avvisi" role="status" aria-live="polite">
      {avvisi.map((a) => (
        <div key={a.id} className={`sb-avviso sb-avviso--${a.esito}`}>
          {a.testo}
        </div>
      ))}
    </div>,
    document.body,
  );
}
